import { Injectable } from '@angular/core';
import { Selector } from '@ngxs/store';
import {
  BasicOptionFn,
  IPlanState,
  PlansState,
} from '@nibtravel/shared/data-access/plans';
import { IQuoteState, QuoteState } from '@nibtravel/shared/data-access/quote';
import {
  IBasePriceSummary,
  IBasicOptionInput,
  IBasicOptionSummary,
  IOptionPriceSummary,
  IPlanOptionsSummary,
  IPlanSummary,
  ITripCancellationOptionSummary,
  OptionType,
} from '@nibtravel/shared/util/models';

@Injectable()
export class OptionsSelector {
  /** Specific selector to fetch the winter sports option price */
  @Selector([QuoteState])
  public static winterSportsPrice(state: IQuoteState): number {
    return this.optionPrice(state, OptionType.SnowSports);
  }

  /** Specific selector to fetch the rental vehicle excess option price */
  @Selector([QuoteState])
  public static rentalVehicleExcessPrice(state: IQuoteState): number {
    return this.optionPrice(state, OptionType.RentalVehicleExcess);
  }

  /** Specific selector to fetch the winter sports option */
  @Selector([QuoteState])
  public static winterSportsOption(
    state: IQuoteState
  ): IBasicOptionInput | undefined {
    return this.basicOption(state, OptionType.SnowSports);
  }

  /** Specific selector to fetch the vary excess option price */
  @Selector([QuoteState])
  public static policyExcessBuyoutPercentagePrice(state: IQuoteState): number {
    return this.optionPrice(state, OptionType.PolicyExcessBuyoutPercentage);
  }

  @Selector([QuoteState])
  public static policyExcessBuyoutPercentageOption(
    state: IQuoteState
  ): IBasicOptionInput | undefined {
    return this.basicOption(state, OptionType.PolicyExcessBuyoutPercentage);
  }

  @Selector([QuoteState])
  public static rentalVehicleExcessOption(
    state: IQuoteState
  ): IBasicOptionInput | undefined {
    return this.basicOption(state, OptionType.RentalVehicleExcess);
  }

  @Selector([QuoteState.planId, PlansState.getPlanSummaries])
  static currencyCode(
    planId: number | undefined,
    planSummaries: Record<number, IPlanSummary> | undefined
  ): string {
    return planId && planSummaries
      ? planSummaries[planId]?.currencyCode
      : 'AUD';
  }

  @Selector([QuoteState])
  public static hasAnyOptions(state: IQuoteState): boolean {
    const basicOptions = !!(
      state.input.basicOptions && state.input.basicOptions.length
    );
    const specifiedItems = !!state.input.specifiedItemOption;
    const adventureOptions = !!state.input.adventureActivitiesOption;
    const cancellationOption = !!state.input.cancellationOption;
    return (
      basicOptions || specifiedItems || adventureOptions || cancellationOption
    );
  }

  @Selector([QuoteState, OptionsSelector.cancellationOptionConfig])
  public static hasCancellationOption(state: IQuoteState): boolean {
    return !!state.input.cancellationOption;
  }

  @Selector([QuoteState.planId, PlansState.basicOptions])
  public static getBasicOptions(
    planId: number | undefined,
    options: BasicOptionFn
  ): IBasicOptionSummary[] | undefined {
    return options(planId);
  }

  @Selector([QuoteState.planId, PlansState.planOptionsSummary])
  static cancellationOptionConfig(
    planId: number | undefined,
    optionsSummary: Record<number, IPlanOptionsSummary> | undefined
  ): ITripCancellationOptionSummary | undefined {
    return planId && optionsSummary
      ? optionsSummary[planId]?.cancellationOption
      : undefined;
  }

  @Selector([PlansState])
  static implicitCancellationValue(planState: IPlanState) {
    return planState.implicitCancellationValue;
  }

  /** Generic abstraction to fetch basic option price */
  public static optionPrice(
    state: IQuoteState,
    optionType: OptionType
  ): number {
    const planId = state.input.planId;
    if (planId) {
      const price = state.prices?.find((p) => p.planId === planId);
      if (price && price.basicOptionPrices) {
        const basicOptionPrice = price.basicOptionPrices.find(
          (opt) => opt.optionType === optionType
        );
        return this.calculatePrice(basicOptionPrice);
      }
    }
    return 0;
  }

  /**
   * The discounts comes as negative number and business required to display
   * values without discount to make it simpler to understand on the quote summary
   */
  public static calculatePrice(
    optPrice: IBasePriceSummary | IOptionPriceSummary | undefined
  ): number {
    if (optPrice) {
      return (
        Math.abs(optPrice.price) +
        Math.abs(optPrice.partnerDiscount) +
        Math.abs(optPrice.campaignDiscount)
      );
    }

    return 0;
  }

  /** Generic abstraction to fetch basic option */
  private static basicOption(
    state: IQuoteState,
    optionType: OptionType
  ): IBasicOptionInput | undefined {
    const basicOptions = state.input.basicOptions;
    const predicate = (option: IBasicOptionInput) =>
      option.optionType === optionType;
    return basicOptions ? basicOptions.find(predicate) : undefined;
  }
}
